import React, { useState, useEffect } from 'react';
import api from '../services/api.js';
import toast from 'react-hot-toast';
import { 
  CheckCircle, XCircle, Download, ExternalLink, 
  DollarSign, FileText, Calendar, User, Eye
} from 'lucide-react';
import Swal from 'sweetalert2';

export default function ConfirmarPagoTramiteModal({ tramite, onClose, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [fileType, setFileType] = useState('');
  const [numeroBoleta, setNumeroBoleta] = useState('');
  const cotizacion = tramite.cotizacion;

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const response = await api.get(`/tramites/${tramite.id}/comprobante`, {
          responseType: 'blob',
          headers: { 'Accept': '*/*' }
        });
        const blob = response.data;
        const url = URL.createObjectURL(blob);
        setImageSrc(url);
        setFileType(blob.type);
      } catch (err) {
        console.error("Error loading receipt image", err);
        toast.error("No se pudo cargar el archivo del comprobante.");
      }
    };
    fetchImage();
    // Cleanup URL on unmount
    return () => {
      if (imageSrc) URL.revokeObjectURL(imageSrc);
    };
  }, [tramite.id]);
  const [boletaFile, setBoletaFile] = useState(null);

  const handleConfirm = async () => {
    const result = await Swal.fire({
      title: '¿Confirmar Pago?',
      text: '¿Está seguro de confirmar este pago? El trámite pasará a estado "Pagado".',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: 'var(--primary)',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, confirmar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      if (boletaFile) {
        formData.append('boleta', boletaFile);
      }
      if (numeroBoleta) {
        formData.append('numero_boleta', numeroBoleta);
      }
      if (cotizacion) {
        formData.append('cotizacion_id', cotizacion.id);
      }
      
      await api.post(`/tramites/${tramite.id}/confirmar-pago`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Pago confirmado exitosamente.');
      onUpdate();
      onClose();
    } catch (err) {
      toast.error('Error al confirmar el pago.');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    const { value: motivo } = await Swal.fire({
      title: 'Rechazar Comprobante',
      input: 'textarea',
      inputLabel: 'Indique el motivo del rechazo',
      inputPlaceholder: 'Ej: El monto depositado es incorrecto, el comprobante es ilegible, etc.',
      inputAttributes: {
        'aria-label': 'Motivo del rechazo'
      },
      showCancelButton: true,
      confirmButtonColor: '#e53e3e',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, rechazar',
      cancelButtonText: 'Cancelar',
      inputValidator: (value) => {
        if (!value || value.trim().length === 0) {
          return 'Debe ingresar un motivo para el rechazo'
        }
      }
    });

    if (!motivo) return;

    setLoading(true);
    try {
      await api.post(`/tramites/${tramite.id}/rechazar-pago`, {
        motivo_rechazo: motivo,
        cotizacion_id: cotizacion?.id
      });
      toast.success('Pago rechazado y notificado al ciudadano.');
      onUpdate();
      onClose();
    } catch (err) {
      toast.error('Error al rechazar el pago.');
    } finally {
      setLoading(false);
    }
  };

  const isImage = fileType.startsWith('image/');
  const isPdf = fileType === 'application/pdf';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '1000px', width: '95%', padding: 0, display: 'flex', flexDirection: 'column', height: '90vh' }} onClick={e => e.stopPropagation()}>
        
        <div className="modal-header" style={{ padding: '1.2rem 1.5rem', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ padding: '10px', background: 'var(--success-bg)', borderRadius: '10px', color: 'var(--success)' }}>
               <DollarSign size={20} />
            </div>
            <div>
              <h3 style={{ marginBottom: '2px' }}>Validación de Pago</h3>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Trámite: {tramite.numero_seguimiento} · {tramite.nombre_solicitante}</div>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left: Info */}
          <div style={{ width: '320px', borderRight: '1px solid var(--border-color)', background: '#f8fafc', overflowY: 'auto', padding: '1.5rem' }}>
            <h4 style={{ fontSize: '0.9rem', color: 'var(--primary-dark)', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Detalles de la Cotización</h4>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ background: 'white', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>Monto Total</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--primary)' }}>
                  ${new Intl.NumberFormat('es-CL').format(cotizacion?.total || 0)}
                </div>
              </div>

              {cotizacion?.items && (
                <div style={{ background: 'white', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '8px' }}>Ítems Detallados</div>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: '0.85rem' }}>
                    {cotizacion.items.map((item, i) => (
                      <li key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: i === cotizacion.items.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                        <span style={{ color: '#475569' }}>{item.descripcion}</span>
                        <span style={{ fontWeight: 600 }}>${new Intl.NumberFormat('es-CL').format(item.monto)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div style={{ fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic', padding: '10px', background: '#fefce8', border: '1px solid #fef08a', borderRadius: '8px' }}>
                Verifique que el monto en el comprobante coincida con el total cotizado antes de confirmar.
              </div>
            </div>
          </div>

          {/* Right: Viewer */}
          <div style={{ flex: 1, background: '#cbd5e1', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
             <div style={{ width: '100%', height: '100%', background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '8px 15px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                   <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>
                    {isPdf ? 'VISTA PREVIA PDF' : isImage ? 'COMPROBANTE ADJUNTO' : 'ARCHIVO ADJUNTO'}
                   </span>
                   {imageSrc && (
                     <a href={imageSrc} target="_blank" rel="noreferrer" className="btn btn-sm" style={{ padding: '4px 8px', fontSize: '0.75rem', background: 'white' }}>
                        <ExternalLink size={12} /> Abrir Original
                     </a>
                   )}
                </div>
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#334155' }}>
                   {imageSrc ? (
                     isPdf ? (
                       <iframe src={imageSrc} title="PDF Preview" style={{ width: '100%', height: '100%', border: 'none' }} />
                     ) : isImage ? (
                       <img 
                        src={imageSrc} 
                        alt="Comprobante de Pago" 
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                       />
                     ) : (
                        <div style={{ color: 'white', textAlign: 'center' }}>
                           <FileText size={64} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                           <h3>Formato no previsualizable</h3>
                           <p>El archivo tiene formato: {fileType}</p>
                           <a href={imageSrc} download="comprobante" className="btn btn-primary" style={{ marginTop: '1rem' }}>Descargar para ver</a>
                        </div>
                     )
                   ) : (
                     <div style={{ color: 'white', textAlign: 'center' }}>
                        <span className="spinner" />
                        <p style={{ marginTop: '10px' }}>Cargando comprobante...</p>
                     </div>
                   )}
                </div>
             </div>
          </div>
        </div>

        <div style={{ padding: '15px 24px', background: '#e2e8f0', borderTop: '1px solid #cbd5e1', display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ fontWeight: 600, color: 'var(--primary-dark)', fontSize: '0.9rem', width: '220px' }}>Número de Boleta (Opcional):</div>
            <input 
              type="text" 
              placeholder="Ej: 123456" 
              value={numeroBoleta}
              onChange={e => setNumeroBoleta(e.target.value)}
              style={{ fontSize: '0.85rem', padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ fontWeight: 600, color: 'var(--primary-dark)', fontSize: '0.9rem', width: '220px' }}>Adjuntar Boleta Electrónica PDF/IMG:</div>
            <input 
              type="file" 
              accept=".pdf,.jpg,.jpeg,.png" 
              onChange={e => setBoletaFile(e.target.files[0])}
              style={{ fontSize: '0.85rem' }}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ padding: '1rem 1.5rem', background: '#f8fafc', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
          <button className="btn btn-danger btn-sm" onClick={handleReject} disabled={loading} style={{ padding: '0.6rem 1.5rem', display: 'flex', gap: '6px', alignItems: 'center' }}>
            {loading ? <span className="spinner" /> : <><XCircle size={16} /> RECHAZAR COMPROBANTE</>}
          </button>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn btn-outline btn-sm" onClick={onClose} disabled={loading}>Cerrar</button>
            <button className="btn btn-success btn-sm" onClick={handleConfirm} disabled={loading} style={{ padding: '0.6rem 1.5rem' }}>
              {loading ? <span className="spinner" /> : <><CheckCircle size={16} /> CONFIRMAR PAGO</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
